import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { useSnackbar } from "notistack";
import {
  CancelSubscription,
  MemberSubscriptionsData,
} from "../../services/MembersService";

interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  subscription: MemberSubscriptionsData | null;
}

export const CancelSubscriptionDialog: React.FC<
  CancelSubscriptionDialogProps
> = ({ open, onClose, onSuccess, subscription }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [revokeAccess, setRevokeAccess] = useState<boolean>(true);
  const [reason, setReason] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const isGranted = subscription?.status === "GRANTED";
  const expiryLabel = subscription?.endDate || "N/A";

  const handleConfirm = async () => {
    if (!subscription) return;

    setLoading(true);
    try {
      await CancelSubscription({
        userSubscriptionId: subscription.id,
        revokeAccess: isGranted ? true : revokeAccess,
        reason: reason || undefined,
      });
      enqueueSnackbar("Subscription cancelled successfully!", {
        variant: "success",
      });
      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      enqueueSnackbar("Failed to cancel subscription", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRevokeAccess(true);
    setReason("");
    onClose();
  };

  if (!subscription) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="cancel-subscription-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="cancel-subscription-dialog-title">
        Cancel Subscription
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <Typography variant="body1" color="textSecondary">
            You are about to cancel the subscription:{" "}
            <strong>{subscription.subscription?.name || "N/A"}</strong>
          </Typography>
        </Box>

        {isGranted ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Confirmation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This is a granted subscription. Cancelling it will revoke access
              immediately.
            </Typography>
          </Box>
        ) : (
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Access Revocation
            </Typography>
            <RadioGroup
              value={revokeAccess ? "immediate" : "grace"}
              onChange={(e) => setRevokeAccess(e.target.value === "immediate")}
            >
              <FormControlLabel
                value="immediate"
                control={<Radio />}
                label="Revoke access immediately"
              />
              <FormControlLabel
                value="grace"
                control={<Radio />}
                label={`Keep access until expiry (${expiryLabel})`}
              />
            </RadioGroup>
          </FormControl>
        )}

        <TextField
          label="Cancellation Reason (Optional)"
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Customer requested refund, System abuse, etc."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Confirm Cancellation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
